import { Icon, IconProps } from '@design/components/icon/Icon';
import {
  getPriceStatus,
  isElementScrollbarVisible,
  PriceStatus,
} from '@helpers';
import {
  Column,
  ColumnDef,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { ReactNode, useMemo, useRef } from 'react';

import { Tooltip } from '../../tooltip/Tooltip';
import { PinConfig } from '../table-cell/TableCell';
import { WithNAFallback } from '../table-cell/WithNAFallback';
import { formatDecimalPlaces } from '../utils';
import styles from './TableV2.module.scss';
import {
  ColumnsTypes,
  StatusTypes,
  statusTypes,
  TableData,
  TableRow,
  TableV2Payload,
} from './TableV2.types';
import { TableV2Cell } from './TableV2Cell';
import { TableV2Header } from './TableV2Header';

const MOBILE_MAX_VISIBLE_ROWS = 5;
const MOBILE_ROW_HEIGHT = 40;

const isStatusTypes = (value: unknown): value is StatusTypes<string> => {
  return typeof value === 'string' && statusTypes.includes(value);
};

type PickedIconProps = Pick<IconProps, 'iconName' | 'fillColor'>;
const styleMapping: Record<StatusTypes<string>, PickedIconProps> = {
  Success: {
    iconName: 'CheckmarkCircle',
    fillColor: 'var(--viz-bullish)',
  },
  Failed: {
    iconName: 'CloseCircle',
    fillColor: 'var(--viz-bearish)',
  },
};

const leftAlignedColumns = ['text', 'status', 'entity'];
export const getCellAlignment = (type: ColumnsTypes) =>
  leftAlignedColumns.includes(type) ? 'left' : 'right';

export type PickedTablePayload = Pick<
  TableV2Payload,
  'columnsMeta' | 'data' | 'stickyHeader'
>;

export type TableV2Props = PickedTablePayload & {
  /** Extra class forwarded to the outer wrapper. */
  className?: string;
  /** Optional content rendered above the table (e.g. title, filters). */
  customHeader?: ReactNode;
  /** Element rendered when `sliceIndex` truncates the data (e.g. a "Show all" button). */
  floatingExpandBtn?: ReactNode;
  /** Show the rounded border around the table wrapper. Defaults to `true`. */
  showBorder?: boolean;
  /** When set, only the first N rows are rendered. Combine with `floatingExpandBtn` for "show first N then expand". */
  sliceIndex?: number;
  /** When true, caps the visible rows on touch devices (5 rows × 40px). Use to keep mobile views compact. */
  isTouchDevice?: boolean;
  /** Called with the TanStack `Row` when any body row is clicked. */
  onRowClick?: (row: Row<TableRow>) => void;
};

/**
 * Modern data table built on `@tanstack/react-table`. Each row is a
 * `TableRow` keyed by column id; columns are described by `columnsMeta`
 * (id, title, `type`, optional `sticky`).
 *
 * Column `type` drives cell rendering: `'number'` formats decimals,
 * `'directional'` colors values bullish/bearish, `'status'` renders a
 * success/failure icon, `'entity'` renders logo + stacked name/ticker,
 * `'text'` renders left-aligned text. Empty
 * cells fall back to the standard "N/A". Cells include built-in
 * tooltips when their value provides one.
 *
 * Use this for new tables. For legacy / SCSS-driven tables, see
 * `Table` + `TableCell` directly. `SmartTable` is deprecated.
 */
export const TableV2 = ({
  className,
  columnsMeta,
  data,
  stickyHeader = false,
  showBorder = true,
  customHeader,
  sliceIndex,
  floatingExpandBtn,
  isTouchDevice = false,
  onRowClick,
}: TableV2Props) => {
  const containerRef = useRef<HTMLTableElement>(null);

  const columns: ColumnDef<TableRow, TableData>[] = columnsMeta.map(item => ({
    id: item.id,
    header: item.title,
    accessorFn: row => row[item.id],
    cell: ({ getValue }) => {
      const value = getValue();
      const priceStatus =
        item.type === 'directional'
          ? getPriceStatus(+value.value)
          : PriceStatus.Default;
      const columnData = value.value;
      const isStatusType = item.type === 'status' && isStatusTypes(columnData);

      const getColumnData = () => {
        if (item.type === 'number') {
          return formatDecimalPlaces(columnData);
        }

        return columnData;
      };

      if (item.type === 'entity') {
        return (
          <div className={styles.entityCell}>
            {value.logo && (
              <img src={value.logo} alt="" className={styles.entityLogo} />
            )}
            <div className={styles.entityText}>
              <WithNAFallback
                value={
                  columnData && (
                    <span className={styles.entityName}>{columnData}</span>
                  )
                }
              />
              {value.tooltip && (
                <span className={styles.entityTicker}>{value.tooltip}</span>
              )}
            </div>
          </div>
        );
      }

      return (
        <Tooltip disabled={!value.tooltip} inPortal label={value.tooltip}>
          <div className={styles.cellInner}>
            {value.logo && <img src={value.logo} alt="logo" />}
            <WithNAFallback
              value={
                columnData && (
                  <div
                    className={styles.cellWrapper}
                    data-price-status={priceStatus}
                    data-status={isStatusType ? columnData : undefined}
                  >
                    {isStatusType && (
                      <Icon
                        iconName={styleMapping[columnData].iconName}
                        size={9}
                        fillColor={styleMapping[columnData].fillColor}
                      />
                    )}
                    {getColumnData()}
                  </div>
                )
              }
            />
          </div>
        </Tooltip>
      );
    },
    meta: item,
  }));

  const hasHorizontalScroll = isElementScrollbarVisible(containerRef);
  const pinnedColumns = columnsMeta.filter(p => p.sticky).map(p => p.id);

  const getIsPinned = (column: Column<TableRow>) =>
    hasHorizontalScroll && !!column.getIsPinned();

  const getPinConfig = (column: Column<TableRow>): PinConfig | undefined =>
    getIsPinned(column)
      ? {
          left: column.getStart('left'),
          right: column.getStart('right'),
          isLastPinned: column.getIsLastColumn('left'),
        }
      : undefined;

  const slicedData = useMemo(() => {
    return sliceIndex !== undefined ? data.slice(0, sliceIndex) : data;
  }, [sliceIndex, data]);

  const tableMaxHeight = useMemo(() => {
    if (slicedData.length > 5 && isTouchDevice) {
      return MOBILE_MAX_VISIBLE_ROWS * MOBILE_ROW_HEIGHT;
    }

    return undefined;
  }, [slicedData.length, isTouchDevice]);

  const table = useReactTable({
    data: slicedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: {
        left: pinnedColumns,
      },
    },
  });

  const { rows } = table.getRowModel();

  return (
    <div className={clsx(styles.container, className)}>
      <div>{customHeader}</div>
      <div
        className={styles.wrapper}
        data-table-v2-wrapper
        data-show-border={showBorder}
        style={
          tableMaxHeight ? { maxHeight: `${tableMaxHeight}px` } : undefined
        }
      >
        <table
          className={styles.styledRevampTableRoot}
          data-table-v2-table
          data-custom-header={!!customHeader || undefined}
          ref={containerRef}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableV2Header
                key={headerGroup.id}
                getPinConfig={getPinConfig}
                headerGroup={headerGroup}
                stickyHeader={!!stickyHeader}
              />
            ))}
          </thead>
          <tbody>
            {rows.map(row => (
              <TableV2Cell key={row.id} row={row} onRowClick={onRowClick} />
            ))}
          </tbody>
        </table>
        {sliceIndex !== undefined && data.length > sliceIndex && (
          <div className={styles.buttonWrapper}>{floatingExpandBtn}</div>
        )}
      </div>
    </div>
  );
};
